import React from 'react';
import { Check, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  PRICING_PLAN_GRADIENTS,
  PRICING_PLAN_ICONS,
  extractApiPlanPackage,
  formatPricingFeatureValue,
  getMonthlyEquivalent,
  getPricingPlanCardShellClass,
  getPricingPlanCopy,
  isPricingFeatureIncluded,
  pricingPlanCardTestId,
  resolvePlanId,
} from '../../lib/pricingPlanDisplay';

export default function PricingPlanCard({
  planId,
  rawPackage,
  locale,
  billingCycle,
  isCurrentPlan,
  featureLabels,
  buttonLabel,
  onSelectPlan,
  paymentsEnabled,
  t,
}) {
  const id = resolvePlanId(planId);
  const apiPlan = extractApiPlanPackage(rawPackage);
  const copy = getPricingPlanCopy(id, locale);
  const isPopular = apiPlan.popular;
  const Icon = PRICING_PLAN_ICONS[id] || PRICING_PLAN_ICONS.BASECAMP;
  const gradient = PRICING_PLAN_GRADIENTS[id] || PRICING_PLAN_GRADIENTS.BASECAMP;
  const monthlyEquivalent = getMonthlyEquivalent(apiPlan, billingCycle);

  const priceSubline =
    billingCycle === 'yearly' && apiPlan.price_yearly > 0
      ? `€${apiPlan.price_yearly} ${t('pricing.billed_annually', 'billed annually')}`
      : apiPlan.price_monthly === 0
        ? t('pricing.free_forever', 'Free forever')
        : null;

  return (
    <article
      className={getPricingPlanCardShellClass({ isPopular, isCurrentPlan })}
      data-testid={pricingPlanCardTestId(id)}
    >
      {isPopular && (
        <div className="pointer-events-none absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap">
          <Badge className="border-0 bg-military-gold px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-950 shadow-sm">
            {t('pricing.most_popular', 'MOST POPULAR')}
          </Badge>
        </div>
      )}

      <header className="mb-5 shrink-0 text-center">
        <div
          className={`mx-auto mb-3 flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${gradient}`}
        >
          <Icon className="h-7 w-7 text-white" />
        </div>
        <h3 className="break-words font-heading text-2xl font-bold leading-tight text-slate-800">
          {copy.name}
        </h3>
        <p className="mt-1 min-h-[2.5rem] break-words text-sm leading-snug text-slate-500">
          {copy.tagline}
        </p>
      </header>

      <div className="mb-5 shrink-0 text-center">
        <div className="flex min-h-[2.25rem] flex-wrap items-baseline justify-center gap-1">
          <span className="text-3xl font-bold text-slate-800">€{monthlyEquivalent}</span>
          <span className="text-slate-500">{t('pricing.per_month', '/mo')}</span>
        </div>
        <p
          className={`mt-1 min-h-[1.25rem] break-words text-sm ${
            priceSubline && apiPlan.price_monthly === 0 ? 'text-emerald-600' : 'text-slate-500'
          }`}
        >
          {priceSubline || '\u00a0'}
        </p>
      </div>

      <p className="mb-4 min-h-[3.25rem] shrink-0 break-words text-sm leading-snug text-slate-600">
        {copy.description}
      </p>

      <ul className="mb-5 min-h-[12rem] flex-1 space-y-2">
        {Object.entries(apiPlan.features).slice(0, 8).map(([key, value]) => {
          const feature = featureLabels[key];
          if (!feature) return null;
          const included = isPricingFeatureIncluded(value);
          return (
            <li key={key} className="flex items-start gap-2 text-sm">
              {included ? (
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
              ) : (
                <X className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              )}
              <span className={`break-words ${included ? 'text-slate-700' : 'text-slate-500'}`}>
                {formatPricingFeatureValue(value, feature.label, t)}
              </span>
            </li>
          );
        })}
      </ul>



      <footer className="mt-auto shrink-0 pt-0">
        <Button
          onClick={() => onSelectPlan(id)}
          disabled={isCurrentPlan}
          className={`min-h-[44px] w-full ${
            isCurrentPlan
              ? 'cursor-default bg-slate-700 text-slate-300'
              : isPopular
                ? 'bg-military-gold text-slate-950 hover:bg-military-gold/90'
                : 'bg-signal-blue text-white hover:bg-signal-blue-hover'
          }`}
          data-testid={
            apiPlan.price_monthly > 0 && !paymentsEnabled
              ? `pricing-contact-${id.toLowerCase()}`
              : `pricing-select-${id.toLowerCase()}`
          }
        >
          {buttonLabel}
        </Button>
      </footer>
    </article>
  );
}
